import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import type { ChatRequestContext } from '../types/request-context';

interface OpenChatPanelOptions extends ChatRequestContext {
  conversationId?: string | null;
}

interface ChatSidePanelContextValue {
  isOpen: boolean;
  conversationId: string | null;
  requestContext: ChatRequestContext | null;
  openChatPanel: (options?: OpenChatPanelOptions) => void;
  closeChatPanel: () => void;
  startNewChat: () => void;
  consumeRequestContext: () => void;
}

const ChatSidePanelContext = createContext<ChatSidePanelContextValue | null>(null);

function getRequestContext(options?: OpenChatPanelOptions): ChatRequestContext | null {
  const requestContext: ChatRequestContext = {};

  if (options?.url?.trim()) {
    requestContext.url = options.url.trim();
  }

  if (options?.context?.trim()) {
    requestContext.context = options.context.trim();
  }

  return Object.keys(requestContext).length > 0 ? requestContext : null;
}

export function ChatSidePanelProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [requestContext, setRequestContext] = useState<ChatRequestContext | null>(null);

  const openChatPanel = useCallback((options?: OpenChatPanelOptions) => {
    setIsOpen(true);
    setConversationId(options?.conversationId ?? null);
    setRequestContext(getRequestContext(options));
  }, []);

  const closeChatPanel = useCallback(() => {
    setIsOpen(false);
  }, []);

  const startNewChat = useCallback(() => {
    setIsOpen(true);
    setConversationId(null);
    setRequestContext(null);
  }, []);

  const consumeRequestContext = useCallback(() => {
    setRequestContext(null);
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      conversationId,
      requestContext,
      openChatPanel,
      closeChatPanel,
      startNewChat,
      consumeRequestContext,
    }),
    [
      isOpen,
      conversationId,
      requestContext,
      openChatPanel,
      closeChatPanel,
      startNewChat,
      consumeRequestContext,
    ]
  );

  return <ChatSidePanelContext.Provider value={value}>{children}</ChatSidePanelContext.Provider>;
}

export function useChatSidePanel() {
  const context = useContext(ChatSidePanelContext);

  if (!context) {
    throw new Error('useChatSidePanel must be used within ChatSidePanelProvider.');
  }

  return context;
}
